"use client";

import { Component, ErrorInfo, ReactNode } from "react";
import Image from "next/image";
import datadog from "@ilg-core/analytics/datadog";
import styles from "./ErrorBoundary.module.css";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    datadog.log.error(
      "Unhandled render error",
      {
        componentStack: errorInfo.componentStack,
        cause: error.cause,
        statusMessage: error.message,
        name: error.name,
        stack: error.stack,
      }
    );
  }

  render() {
    if (this.state.hasError) {
      return (
        <div data-testid="error-page" className={styles.errorPage}>
          <Image
            src={"/assets/images/info-triangle.svg"}
            alt={"error"}
            width={64}
            height={64}
          />
          <div className={styles.title}>Something went wrong</div>
          <div className={styles.message}>
            <p className={styles.messageText}>It looks like there’s a glitch in the system. Just refresh to get things up and running again.</p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
